import React, { useEffect, useState, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  TablePagination,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Chip,
  Grid,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import { useNavigate } from 'react-router-dom';
import PDFViewer from '../shared/PDFViewer';

interface OrdenPago {
  nroFormulario: number;
  fechaEmision: string;
  nroProveedor: number;
  nombre: string;
  importeTotal: number;
  formaDePago: string;
  usuario: string;
  cae_Nro: string;
  cae_Vencimiento: string;
  tipoComprobante: string;
  nroSuc: number;
  descripcion: string;
  cuit: string;
  letra?: string;
  nroComprobante?: number;
}

interface OrdenesPagoResponse {
  items: OrdenPago[];
  totalCount: number;
}

const OrdenPago: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  // Obtener el primer y último día del mes actual
  const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  };

  const getLastDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  };

  const [ordenesPago, setOrdenesPago] = useState<OrdenPago[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [fechaDesde, setFechaDesde] = useState<string>(() => {
    const fecha = getFirstDayOfMonth();
    return fecha.toISOString().split('T')[0];
  });
  const [fechaHasta, setFechaHasta] = useState<string>(() => {
    const fecha = getLastDayOfMonth();
    return fecha.toISOString().split('T')[0];
  });
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedOrdenPago, setSelectedOrdenPago] = useState<number | null>(null);

  // Función para convertir fecha de YYYY-MM-DD a DD-MM-YYYY
  const formatFechaParaAPI = (fecha: string): string => {
    if (!fecha) return '';
    const [year, month, day] = fecha.split('-');
    return `${day}-${month}-${year}`;
  };

  const fetchOrdenesPago = async (pageNumber: number, pageSize: number, search: string, desde: string, hasta: string) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        enqueueSnackbar('No hay token de autenticación. Por favor, inicie sesión nuevamente.', { 
          variant: 'error',
          autoHideDuration: 3000
        });
        return;
      }

      const params: any = {
        pageNumber: pageNumber + 1,
        pageSize,
        searchTerm: search || null
      };

      if (desde) {
        params.FechaDesde = formatFechaParaAPI(desde);
      }
      if (hasta) {
        params.FechaHasta = formatFechaParaAPI(hasta);
      }

      const response = await api.get<OrdenesPagoResponse>(
        '/api/Tesoreria/ordendepago',
        {
          params
        }
      );

      // Manejar diferentes formatos de respuesta
      let items: OrdenPago[] = [];
      let totalCount = 0;

      if (Array.isArray(response.data)) {
        // Si es un array directo
        items = response.data.map((item: any) => ({
          nroFormulario: item.nroFormulario || 0,
          fechaEmision: item.fechaEmision || '',
          nroProveedor: item.nroProveedor || item.nroCliente || 0,
          nombre: item.nombre || '',
          importeTotal: item.importeTotal || 0,
          formaDePago: item.formaDePago || '',
          usuario: item.usuario || '',
          cae_Nro: item.cae_Nro || item.caE_Nro || '',
          cae_Vencimiento: item.cae_Vencimiento || item.caE_Vencimiento || '',
          tipoComprobante: item.tipoComprobante || item.nroTipoFormulario || '',
          nroSuc: item.nroSuc || 0,
          descripcion: item.descripcion || '',
          cuit: item.cuit || '',
          letra: item.letra || '',
          nroComprobante: item.nroComprobante || 0
        }));
        totalCount = items.length;
      } else if (response.data && Array.isArray(response.data.items)) {
        // Si tiene estructura de paginación
        items = response.data.items.map((item: any) => ({
          nroFormulario: item.nroFormulario || 0,
          fechaEmision: item.fechaEmision || '',
          nroProveedor: item.nroProveedor || item.nroCliente || 0,
          nombre: item.nombre || '',
          importeTotal: item.importeTotal || 0,
          formaDePago: item.formaDePago || '',
          usuario: item.usuario || '',
          cae_Nro: item.cae_Nro || item.caE_Nro || '',
          cae_Vencimiento: item.cae_Vencimiento || item.caE_Vencimiento || '',
          tipoComprobante: item.tipoComprobante || item.nroTipoFormulario || '',
          nroSuc: item.nroSuc || 0,
          descripcion: item.descripcion || '',
          cuit: item.cuit || '',
          letra: item.letra || '',
          nroComprobante: item.nroComprobante || 0
        }));
        totalCount = response.data.totalCount || items.length;
      }

      setOrdenesPago(items);
      setTotalRecords(totalCount);
    } catch (error: any) {
      const status = error?.response?.status;
      
      if (status === 404 || status === 204) {
        console.log('No hay órdenes de pago disponibles');
        setOrdenesPago([]);
        setTotalRecords(0);
      } else {
        console.error('Error detallado:', error);
        const errorMessage = error.response?.data?.message || 
                           error.response?.data || 
                           'Error al cargar las órdenes de pago';
        enqueueSnackbar(errorMessage, { 
          variant: 'error',
          autoHideDuration: 3000
        });
        setOrdenesPago([]);
        setTotalRecords(0);
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchOrdenesPago(page, rowsPerPage, searchTerm, fechaDesde, fechaHasta);
  }, [page, rowsPerPage, searchTerm, fechaDesde, fechaHasta]);


  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Mantener siempre 10 filas por página
    const newRowsPerPage = parseInt(event.target.value, 10);
    if (newRowsPerPage !== 10) {
      // Si intentan cambiar, mantener en 10
      setRowsPerPage(10);
    } else {
      setRowsPerPage(10);
    }
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleNuevaOrdenPago = () => {
    navigate('/tesoreria/orden-pago/nuevo');
  };

  const handleImprimirPDF = async (nroFormulario: number) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        enqueueSnackbar('No hay token de autenticación', { variant: 'error' });
        return;
      }

      console.log('🖨️ Iniciando generación de PDF para orden de pago:', nroFormulario);

      // Variables para medios de pago y documentos aplicados
      let mediosDePago: any[] = [];
      let documentosAplicar: any[] = [];
      let ordenPago: any = null;
      
      // Obtener los datos principales de la orden desde el endpoint principal
      // Luego obtener los medios de pago desde el endpoint de detalle
      let datosPrincipales: any = null;
      let detalleObtenido = false;
      
      // Primero intentar obtener los datos principales desde /api/Tesoreria/ordendepago/{id}
      try {
        console.log(`🔍 Obteniendo datos principales desde: /api/Tesoreria/ordendepago/${nroFormulario}`);
        const principalResponse = await api.get(`/api/Tesoreria/ordendepago/${nroFormulario}`);
        datosPrincipales = principalResponse.data;
        console.log('✅ Datos principales obtenidos:', JSON.stringify(datosPrincipales, null, 2));
        console.log('📦 Campos disponibles en datos principales:', Object.keys(datosPrincipales || {}));
      } catch (error: any) {
        console.log(`⚠️ Endpoint principal falló:`, error?.response?.status || error?.message);
      }
      
      // Luego obtener el detalle con los medios de pago desde /api/Tesoreria/ordendepago/{id}/detalle
      try {
        console.log(`🔍 Obteniendo detalle desde: /api/Tesoreria/ordendepago/${nroFormulario}/detalle`);
        const detalleResponse = await api.get(`/api/Tesoreria/ordendepago/${nroFormulario}/detalle`);
        const detalleCompleto = detalleResponse.data;
        
        if (detalleCompleto) {
          console.log('✅ Detalle obtenido:', JSON.stringify(detalleCompleto, null, 2));
          console.log('📦 Campos disponibles en detalle:', Object.keys(detalleCompleto));
          
          // Combinar datos principales con el detalle (el detalle tiene prioridad)
          const datosCombinados = { ...datosPrincipales, ...detalleCompleto };
          
          // Calcular importeTotal sumando los importes de los medios de pago si no viene directamente
          let importeTotalCalculado = datosCombinados.importeTotal || datosPrincipales?.importeTotal || 0;
          if (!importeTotalCalculado && detalleCompleto.mediosDePago && Array.isArray(detalleCompleto.mediosDePago)) {
            importeTotalCalculado = detalleCompleto.mediosDePago.reduce((sum: number, medio: any) => sum + (medio.importe || 0), 0);
          } else if (!importeTotalCalculado && detalleCompleto.mediosDeCobro && Array.isArray(detalleCompleto.mediosDeCobro)) {
            importeTotalCalculado = detalleCompleto.mediosDeCobro.reduce((sum: number, medio: any) => sum + (medio.importe || 0), 0);
          } else if (!importeTotalCalculado && detalleCompleto.medios && Array.isArray(detalleCompleto.medios)) {
            importeTotalCalculado = detalleCompleto.medios.reduce((sum: number, medio: any) => sum + (medio.importe || 0), 0);
          }
          
          ordenPago = {
            nroFormulario: datosCombinados.nroFormulario || datosPrincipales?.nroFormulario || nroFormulario,
            fechaEmision: datosCombinados.fechaEmision || datosPrincipales?.fechaEmision || datosCombinados.fecha || datosCombinados.fechaDocumento || '',
            nroProveedor: datosCombinados.nroProveedor || datosCombinados.nroCliente || datosPrincipales?.nroProveedor || datosPrincipales?.nroCliente || datosCombinados.proveedor || datosCombinados.cliente || 0,
            nombre: datosCombinados.nombre || datosPrincipales?.nombre || datosCombinados.nombreProveedor || datosCombinados.nombreCliente || datosCombinados.razonSocial || '',
            importeTotal: importeTotalCalculado,
            formaDePago: datosCombinados.formaDePago || datosPrincipales?.formaDePago || datosCombinados.formaPago || '',
            usuario: datosCombinados.usuario || datosPrincipales?.usuario || datosCombinados.user || '',
            cae_Nro: datosCombinados.cae_Nro || datosCombinados.caE_Nro || datosCombinados.caeNro || datosPrincipales?.cae_Nro || datosPrincipales?.caE_Nro || '',
            cae_Vencimiento: datosCombinados.cae_Vencimiento || datosCombinados.caE_Vencimiento || datosCombinados.caeVencimiento || datosPrincipales?.cae_Vencimiento || datosPrincipales?.caE_Vencimiento || '',
            tipoComprobante: datosCombinados.tipoComprobante || datosCombinados.nroTipoFormulario || datosCombinados.tipo || datosPrincipales?.tipoComprobante || datosPrincipales?.nroTipoFormulario || '',
            nroSuc: datosCombinados.nroSuc || datosCombinados.sucursal || datosCombinados.nroSucursal || datosPrincipales?.nroSuc || 0,
            descripcion: datosCombinados.descripcion || datosPrincipales?.descripcion || datosCombinados.concepto || '',
            cuit: datosCombinados.cuit || datosPrincipales?.cuit || datosCombinados.cuitProveedor || datosCombinados.cuitCliente || '',
            letra: datosCombinados.letra || datosCombinados.letraComprobante || datosPrincipales?.letra || '',
            nroComprobante: datosCombinados.nroComprobante || datosCombinados.numeroComprobante || datosCombinados.comprobante || datosPrincipales?.nroComprobante || 0
          };
          
          console.log('✅ ordenPago construido desde datos combinados:', JSON.stringify(ordenPago, null, 2));
          
          // Obtener medios de pago y documentos aplicados del detalle
          mediosDePago = detalleCompleto.mediosDePago || detalleCompleto.mediosDeCobro || detalleCompleto.medios || [];
          
          // Intentar obtener documentos aplicados de diferentes campos posibles
          documentosAplicar = detalleCompleto.documentosAplicar || 
                             detalleCompleto.documentos || 
                             detalleCompleto.comprobantes || 
                             detalleCompleto.comprobantesDeuda ||
                             detalleCompleto.comprobantesAplicados ||
                             datosPrincipales?.documentosAplicar ||
                             datosPrincipales?.documentos ||
                             [];
          
          console.log('📋 Medios de pago del detalle (raw):', JSON.stringify(mediosDePago, null, 2));
          console.log('📋 Documentos aplicados del detalle (raw):', JSON.stringify(documentosAplicar, null, 2));
          console.log('📋 Campos disponibles en detalleCompleto relacionados con documentos:', Object.keys(detalleCompleto).filter(k => 
            k.toLowerCase().includes('documento') || 
            k.toLowerCase().includes('comprobante') || 
            k.toLowerCase().includes('aplicar')
          ));
          
          // Si no hay documentos aplicados en el detalle, intentar obtenerlos del endpoint principal
          if ((!documentosAplicar || documentosAplicar.length === 0) && datosPrincipales) {
            console.log('⚠️ No se encontraron documentos aplicados en el detalle, buscando en datos principales...');
            documentosAplicar = datosPrincipales.documentosAplicar || 
                               datosPrincipales.documentos || 
                               datosPrincipales.comprobantes || 
                               [];
            console.log('📋 Documentos aplicados desde datos principales:', JSON.stringify(documentosAplicar, null, 2));
          }
          
          // Si los medios de pago no tienen tipoPago, intentar agregarlo basándose en los objetos anidados
          mediosDePago = mediosDePago.map((medio: any) => {
            // Limpiar el tipoPago si viene con espacios
            if (medio.tipoPago && typeof medio.tipoPago === 'string') {
              medio.tipoPago = medio.tipoPago.trim();
            }
            
            // Si no tiene tipoPago pero tiene objetos anidados, inferirlo
            if (!medio.tipoPago || medio.tipoPago.length <= 2) {
              if (medio.cheque) {
                medio.tipoPago = 'Cheque';
              } else if (medio.tarjeta) {
                medio.tipoPago = 'Tarjeta';
              } else if (medio.mcp) {
                const mcpStr = String(medio.mcp).toUpperCase();
                if (mcpStr.includes('EFECTIVO') || mcpStr.includes('CAJA CHICA')) {
                  medio.tipoPago = 'Efectivo';
                } else if (mcpStr.includes('TRANSFERENCIA') || mcpStr.includes('BANCARIA') || mcpStr.includes('BANCO')) {
                  medio.tipoPago = 'Transferencia';
                } else if (mcpStr.includes('CHEQUE')) {
                  medio.tipoPago = 'Cheque';
                } else if (mcpStr.includes('TARJETA')) {
                  medio.tipoPago = 'Tarjeta';
                } else if (mcpStr.includes('OTROS')) {
                  medio.tipoPago = 'Otros';
                }
              } else if (medio.tipoPago) {
                // Si tiene tipoPago pero es muy corto (como "E"), convertirlo
                const tipoUpper = String(medio.tipoPago).trim().toUpperCase();
                if (tipoUpper === 'E') {
                  medio.tipoPago = 'Efectivo';
                } else if (tipoUpper === 'TB' || tipoUpper === 'T') {
                  medio.tipoPago = 'Transferencia';
                } else if (tipoUpper === 'C') {
                  medio.tipoPago = 'Cheque';
                } else if (tipoUpper === 'TA' || tipoUpper === 'TARJ') {
                  medio.tipoPago = 'Tarjeta';
                } else if (tipoUpper === 'O') {
                  medio.tipoPago = 'Otros';
                }
              }
            }
            return medio;
          });
          
          console.log('📋 Medios de pago después de enriquecer:', JSON.stringify(mediosDePago, null, 2));
          detalleObtenido = true;
        }
      } catch (error: any) {
        console.log(`⚠️ Endpoint de detalle falló:`, error?.response?.status || error?.message);
        
        // Si el detalle falla pero tenemos datos principales, usarlos
        if (datosPrincipales) {
          ordenPago = {
            nroFormulario: datosPrincipales.nroFormulario || nroFormulario,
            fechaEmision: datosPrincipales.fechaEmision || datosPrincipales.fecha || '',
            nroProveedor: datosPrincipales.nroProveedor || datosPrincipales.nroCliente || 0,
            nombre: datosPrincipales.nombre || '',
            importeTotal: datosPrincipales.importeTotal || 0,
            formaDePago: datosPrincipales.formaDePago || '',
            usuario: datosPrincipales.usuario || '',
            cae_Nro: datosPrincipales.cae_Nro || datosPrincipales.caE_Nro || '',
            cae_Vencimiento: datosPrincipales.cae_Vencimiento || datosPrincipales.caE_Vencimiento || '',
            tipoComprobante: datosPrincipales.tipoComprobante || datosPrincipales.nroTipoFormulario || '',
            nroSuc: datosPrincipales.nroSuc || 0,
            descripcion: datosPrincipales.descripcion || '',
            cuit: datosPrincipales.cuit || '',
            letra: datosPrincipales.letra || '',
            nroComprobante: datosPrincipales.nroComprobante || 0
          };
          detalleObtenido = true;
        }
      }
      
      // Si ningún endpoint funcionó, usar los datos del listado
      if (!detalleObtenido) {
        console.warn('⚠️ No se pudo obtener el detalle completo desde ningún endpoint, usando datos del listado');
        const ordenPagoFromList = ordenesPago.find(op => op.nroFormulario === nroFormulario);
        
        if (!ordenPagoFromList) {
          enqueueSnackbar('No se encontró la orden de pago', { variant: 'error' });
          return;
        }
        
        // Intentar obtener los datos completos del listado original (allRawItems)
        // para ver si hay información adicional que no se mapeó
        // Buscar en todas las páginas del listado
        try {
          const pageSize = 100;
          let allRawItems: any[] = [];
          let pageNumber = 1;
          let totalPages = 1;
          let found = false;

          // Primera llamada para obtener la primera página y saber cuántas páginas hay
          const firstResponse = await api.get('/api/Tesoreria/ordendepago', {
            params: { pageNumber: 1, pageSize }
          });
          const firstData = firstResponse.data;
          
          if (Array.isArray(firstData)) {
            allRawItems = firstData;
            found = true;
          } else if (firstData && Array.isArray(firstData.items)) {
            allRawItems = firstData.items || [];
            const totalCount = firstData.totalCount || 0;
            const totalPages = firstData.totalPages || Math.ceil(totalCount / pageSize) || 1;
            
            // Si hay más páginas, cargarlas todas
            if (totalPages > 1) {
              const pagePromises = [];
              for (let p = 2; p <= totalPages; p++) {
                pagePromises.push(
                  api.get('/api/Tesoreria/ordendepago', {
                    params: { pageNumber: p, pageSize }
                  }).then(res => {
                    const pageData = res.data;
                    if (Array.isArray(pageData)) {
                      return pageData;
                    } else if (pageData && Array.isArray(pageData.items)) {
                      return pageData.items;
                    }
                    return [];
                  }).catch(err => {
                    console.error(`❌ Error cargando página ${p}:`, err);
                    return [];
                  })
                );
              }
              
              const allPages = await Promise.all(pagePromises);
              allPages.forEach((pageItems) => {
                allRawItems.push(...pageItems);
              });
            }
            found = true;
          }
          
          const rawItem = allRawItems.find((item: any) => item.nroFormulario === nroFormulario);
          if (rawItem) {
            console.log('📦 Datos completos del item del listado:', JSON.stringify(rawItem, null, 2));
            console.log('📦 Campos disponibles en el item:', Object.keys(rawItem));
            console.log('📦 rawItem.nroFormulario:', rawItem.nroFormulario);
            console.log('📦 rawItem.fechaEmision:', rawItem.fechaEmision);
            console.log('📦 rawItem.nroProveedor:', rawItem.nroProveedor);
            console.log('📦 rawItem.nroCliente:', rawItem.nroCliente);
            console.log('📦 rawItem.importeTotal:', rawItem.importeTotal);
            console.log('📦 rawItem.cuit:', rawItem.cuit);
            console.log('📦 rawItem.nroComprobante:', rawItem.nroComprobante);
            
            // Construir ordenPago enriquecido con todos los datos disponibles del rawItem
            ordenPago = {
              nroFormulario: rawItem.nroFormulario || ordenPagoFromList.nroFormulario || nroFormulario,
              fechaEmision: rawItem.fechaEmision || ordenPagoFromList.fechaEmision || '',
              nroProveedor: rawItem.nroProveedor || rawItem.nroCliente || ordenPagoFromList.nroProveedor || 0,
              nombre: rawItem.nombre || ordenPagoFromList.nombre || '',
              importeTotal: rawItem.importeTotal || rawItem.importe || ordenPagoFromList.importeTotal || 0,
              formaDePago: rawItem.formaDePago || ordenPagoFromList.formaDePago || '',
              usuario: rawItem.usuario || ordenPagoFromList.usuario || '',
              cae_Nro: rawItem.cae_Nro || rawItem.caE_Nro || ordenPagoFromList.cae_Nro || '',
              cae_Vencimiento: rawItem.cae_Vencimiento || rawItem.caE_Vencimiento || ordenPagoFromList.cae_Vencimiento || '',
              tipoComprobante: rawItem.tipoComprobante || rawItem.nroTipoFormulario || ordenPagoFromList.tipoComprobante || '',
              nroSuc: rawItem.nroSuc || ordenPagoFromList.nroSuc || 0,
              descripcion: rawItem.descripcion || ordenPagoFromList.descripcion || '',
              cuit: rawItem.cuit || ordenPagoFromList.cuit || '',
              letra: rawItem.letra || ordenPagoFromList.letra || '',
              nroComprobante: rawItem.nroComprobante || ordenPagoFromList.nroComprobante || 0
            };
            
            console.log('✅ ordenPago construido:', JSON.stringify(ordenPago, null, 2));
            
            // Intentar extraer medios de pago del item raw si existen
            if (rawItem.mediosDePago || rawItem.mediosDeCobro || rawItem.medios) {
              mediosDePago = rawItem.mediosDePago || rawItem.mediosDeCobro || rawItem.medios || [];
              console.log('✅ Medios de pago encontrados en el listado:', mediosDePago);
            }
            if (rawItem.documentosAplicar || rawItem.documentos) {
              documentosAplicar = rawItem.documentosAplicar || rawItem.documentos || [];
            }
          } else {
            console.warn('⚠️ No se encontró el rawItem en el listado, usando datos mapeados');
            console.log('📋 ordenPagoFromList:', JSON.stringify(ordenPagoFromList, null, 2));
            // Si no encontramos el rawItem, usar los datos del listado mapeado
            ordenPago = ordenPagoFromList;
          }
        } catch (error) {
          console.warn('⚠️ No se pudo obtener el listado completo para buscar medios de pago:', error);
          // Si falla, usar los datos del listado mapeado
          ordenPago = ordenPagoFromList;
        }
        
        // Enriquecer medios de pago si los encontramos
        if (mediosDePago.length > 0) {
          mediosDePago = mediosDePago.map((medio: any) => {
            if (!medio.tipoPago) {
              if (medio.cheque) {
                medio.tipoPago = 'Cheque';
              } else if (medio.tarjeta) {
                medio.tipoPago = 'Tarjeta';
              } else if (medio.mcp) {
                const mcpStr = String(medio.mcp).toUpperCase();
                if (mcpStr.includes('EFECTIVO') || mcpStr.includes('CAJA CHICA')) {
                  medio.tipoPago = 'Efectivo';
                } else if (mcpStr.includes('TRANSFERENCIA') || mcpStr.includes('BANCARIA') || mcpStr.includes('BANCO')) {
                  medio.tipoPago = 'Transferencia';
                } else if (mcpStr.includes('CHEQUE')) {
                  medio.tipoPago = 'Cheque';
                } else if (mcpStr.includes('TARJETA')) {
                  medio.tipoPago = 'Tarjeta';
                } else if (mcpStr.includes('OTROS')) {
                  medio.tipoPago = 'Otros';
                }
              }
            }
            return medio;
          });
        }
        
        // Mostrar advertencia si no hay medios de pago
        if (mediosDePago.length === 0) {
          console.warn('⚠️ No se pudieron obtener los medios de pago. El PDF se generará sin esa información.');
          enqueueSnackbar('Advertencia: No se pudieron obtener los medios de pago. El PDF se generará sin esa información.', { 
            variant: 'warning',
            autoHideDuration: 5000
          });
        }
      }

      if (!ordenPago) {
        enqueueSnackbar('No se encontró la orden de pago', { variant: 'error' });
        return;
      }

      console.log('✅ Datos de la orden de pago obtenidos:', ordenPago);
      console.log('📋 Medios de pago finales:', mediosDePago);
      console.log('📋 Documentos aplicados finales:', documentosAplicar);
      console.log('🔍 Verificación de campos antes de generar PDF:');
      console.log('  - nroFormulario:', ordenPago.nroFormulario);
      console.log('  - fechaEmision:', ordenPago.fechaEmision);
      console.log('  - nroProveedor:', ordenPago.nroProveedor);
      console.log('  - nombre:', ordenPago.nombre);
      console.log('  - importeTotal:', ordenPago.importeTotal);
      console.log('  - cuit:', ordenPago.cuit);
      console.log('  - nroComprobante:', ordenPago.nroComprobante);

      // Generar PDF en el frontend
      console.log('📄 Cargando librerías de PDF...');
      let jsPDF: any;
      let autoTable: any;
      
      try {
        const jsPDFModule = await import('jspdf');
        jsPDF = jsPDFModule.jsPDF;
        const autoTableModule = await import('jspdf-autotable');
        autoTable = autoTableModule.default;
        console.log('✅ Librerías de PDF cargadas correctamente');
      } catch (error: any) {
        console.error('❌ Error al cargar librerías de PDF:', error);
        enqueueSnackbar('Error al cargar las librerías necesarias para generar el PDF', { variant: 'error' });
        return;
      }

      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const marginX = 40;
      let currentY = 50;

      // Título "ORDEN DE PAGO" centrado (igual que "RECIBO")
      doc.setFontSize(28);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      const titleText = 'ORDEN DE PAGO';
      const titleWidth = doc.getTextWidth(titleText);
      doc.text(titleText, (pageWidth - titleWidth) / 2, currentY);
      currentY += 50;

      // Datos de la orden de pago (formato exacto como el recibo)
      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);

      const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('es-AR', { 
          style: 'currency', 
          currency: 'ARS', 
          minimumFractionDigits: 2 
        }).format(value);
      };

      const formatDate = (dateStr: string | null | undefined) => {
        if (!dateStr) return '';
        try {
          // Intentar parsear la fecha en diferentes formatos
          let date: Date;
          if (typeof dateStr === 'string') {
            // Si ya está en formato ISO o similar
            date = new Date(dateStr);
            // Si el parseo falla, intentar con formato DD/MM/YYYY
            if (isNaN(date.getTime())) {
              const parts = dateStr.split('/');
              if (parts.length === 3) {
                date = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
              } else {
                return dateStr; // Devolver el string original si no se puede parsear
              }
            }
          } else {
            return '';
          }
          
          if (isNaN(date.getTime())) {
            return dateStr;
          }
          
          return date.toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        } catch {
          return dateStr || '';
        }
      };

      // Datos alineados a la izquierda
      doc.text(`Número: ${ordenPago.nroFormulario?.toString() || ''}`, marginX, currentY);
      currentY += 20;
      
      doc.text(`Fecha: ${formatDate(ordenPago.fechaEmision)}`, marginX, currentY);
      currentY += 20;
      
      doc.text(`Proveedor: ${ordenPago.nroProveedor?.toString() || ''}`, marginX, currentY);
      currentY += 20;
      
      doc.text(`CUIT: ${ordenPago.cuit || ''}`, marginX, currentY);
      currentY += 20;
      
      if (ordenPago.nroComprobante) {
        doc.text(`Nro. Comprobante: ${ordenPago.nroComprobante?.toString() || ''}`, marginX, currentY);
        currentY += 20;
      }
      
      doc.text(`Importe Total: ${formatCurrency(ordenPago.importeTotal || 0)}`, marginX, currentY);
      currentY += 30;

      // Medios de Pago (título)
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Medios de Pago', marginX, currentY);
      currentY += 20;

      // Función para obtener el nombre completo del tipo de pago (igual que en Recibo)
      const getTipoPagoNombre = (medio: any): string => {
        // Prioridad 1: Si tiene objeto cheque, es Cheque
        if (medio.cheque) {
          return 'Cheque';
        }
        
        // Prioridad 2: Si tiene objeto tarjeta, es Tarjeta
        if (medio.tarjeta) {
          return 'Tarjeta';
        }
        
        // Prioridad 3: Si viene el campo tipoPago con nombre completo
        if (medio.tipoPago) {
          const tipoPagoStr = String(medio.tipoPago).trim();
          const tipoUpper = tipoPagoStr.toUpperCase();
          
          // Si es un nombre completo (más de 2 caracteres), usarlo directamente
          if (tipoPagoStr.length > 2 && !['E', 'TB', 'C', 'T', 'O'].includes(tipoUpper)) {
            return tipoPagoStr.charAt(0).toUpperCase() + tipoPagoStr.slice(1).toLowerCase();
          }
          
          // Si es un código, mapearlo
          if (tipoUpper === 'E' || tipoUpper.includes('EFECTIVO')) {
            return 'Efectivo';
          } else if (tipoUpper === 'TB' || tipoUpper.includes('TRANSFERENCIA')) {
            return 'Transferencia';
          } else if (tipoUpper === 'C' || tipoUpper.includes('CHEQUE')) {
            return 'Cheque';
          } else if (tipoUpper === 'TA' || tipoUpper === 'TARJ' || tipoUpper.includes('TARJETA')) {
            return 'Tarjeta';
          } else if (tipoUpper === 'O' || tipoUpper.includes('OTROS')) {
            return 'Otros';
          }
        }
        
        // Prioridad 4: Intentar inferir del mcp
        if (medio.mcp) {
          const mcpStr = String(medio.mcp).toUpperCase();
          if (mcpStr.includes('EFECTIVO') || mcpStr.includes('CAJA CHICA')) {
            return 'Efectivo';
          } else if (mcpStr.includes('TRANSFERENCIA') || mcpStr.includes('BANCARIA') || mcpStr.includes('BANCO')) {
            return 'Transferencia';
          } else if (mcpStr.includes('CHEQUE')) {
            return 'Cheque';
          } else if (mcpStr.includes('TARJETA')) {
            return 'Tarjeta';
          } else if (mcpStr.includes('OTROS')) {
            return 'Otros';
          }
        }
        
        // Prioridad 5: Si viene en el campo tipo
        if (medio.tipo) {
          const tipoStr = String(medio.tipo).trim();
          const tipoUpper = tipoStr.toUpperCase();
          
          if (tipoStr.length > 2 && !['E', 'TB', 'C', 'T', 'O'].includes(tipoUpper)) {
            return tipoStr.charAt(0).toUpperCase() + tipoStr.slice(1).toLowerCase();
          }
          
          if (tipoUpper === 'E' || tipoUpper.includes('EFECTIVO')) {
            return 'Efectivo';
          } else if (tipoUpper === 'TB' || tipoUpper.includes('TRANSFERENCIA')) {
            return 'Transferencia';
          } else if (tipoUpper === 'C' || tipoUpper.includes('CHEQUE')) {
            return 'Cheque';
          } else if (tipoUpper === 'TA' || tipoUpper === 'TARJ' || tipoUpper.includes('TARJETA')) {
            return 'Tarjeta';
          } else if (tipoUpper === 'O' || tipoUpper.includes('OTROS')) {
            return 'Otros';
          }
        }
        
        // Por defecto
        return 'Efectivo';
      };

      // Tabla de Medios de Pago (con o sin datos)
      const mediosData = mediosDePago && mediosDePago.length > 0
        ? mediosDePago.map((medio: any) => {
            // Log detallado para debug
            console.log('🔍 Medio de pago completo recibido del backend:', JSON.stringify(medio, null, 2));
            console.log('🔍 Campos disponibles en medio:', Object.keys(medio));
            console.log('🔍 medio.tipoPago:', medio.tipoPago);
            console.log('🔍 medio.tipo:', medio.tipo);
            console.log('🔍 medio.mcp:', medio.mcp);
            console.log('🔍 medio.cheque:', medio.cheque);
            console.log('🔍 medio.tarjeta:', medio.tarjeta);
            
            const tipoNombre = getTipoPagoNombre(medio);
            console.log('✅ Tipo de pago determinado:', tipoNombre);
            
            return [
              tipoNombre,
              (medio.nroComprobante || medio.nroComprobanteTa || medio.numeroComprobante || 0).toString(),
              (medio.nroSuc || medio.sucursal || medio.nroSucursal || 0).toString(),
              formatCurrency(medio.importe || medio.monto || 0)
            ];
          })
        : [];

      autoTable(doc, {
        startY: currentY,
        head: [['Tipo', 'Nro. Comprobante', 'Sucursal', 'Importe']],
        body: mediosData,
        theme: 'grid',
        headStyles: { 
          fillColor: [255, 255, 255], 
          textColor: [0, 0, 0], 
          fontStyle: 'bold', 
          halign: 'center',
          lineWidth: 0.5
        },
        bodyStyles: { 
          halign: 'center',
          lineWidth: 0.5
        },
        margin: { left: marginX, right: marginX },
        styles: { 
          fontSize: 10, 
          cellPadding: 5,
          lineWidth: 0.5,
          lineColor: [0, 0, 0]
        },
        tableLineWidth: 0.5,
        tableLineColor: [0, 0, 0]
      });

      currentY = (doc as any).lastAutoTable.finalY + 20;

      // Documentos Aplicados (título)
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Documentos Aplicados', marginX, currentY);
      currentY += 20;

      // Tabla de Documentos Aplicados (con o sin datos)
      console.log('📋 Documentos aplicados antes de mapear para PDF:', JSON.stringify(documentosAplicar, null, 2));
      
      const documentosData = documentosAplicar && documentosAplicar.length > 0
        ? documentosAplicar.map((docItem: any) => {
            console.log('📄 Procesando documento aplicado:', JSON.stringify(docItem, null, 2));
            console.log('📄 Campos disponibles en documento:', Object.keys(docItem));
            
            return [
              docItem.tipoComprobante || docItem.tipo || docItem.tipoComprobanteStr || '',
              docItem.letra || docItem.letraComprobante || '',
              (docItem.nroComprobante || docItem.numeroComprobante || docItem.comprobante || docItem.nroComprobanteStr || 0).toString(),
              formatDate(docItem.fechaEmision || docItem.fecha || docItem.fechaDocumento || ''),
              formatDate(docItem.fechaVencimiento || docItem.fechaVenc || docItem.fechaVencimientoStr || ''),
              formatCurrency(docItem.montoCancelado || docItem.importe || docItem.monto || docItem.saldo || 0)
            ];
          })
        : [];
      
      console.log('📋 Documentos data mapeados para tabla:', documentosData);

      autoTable(doc, {
        startY: currentY,
        head: [['Tipo', 'Letra', 'Nro. Comprobante', 'Fecha Emisión', 'Fecha Vencimiento', 'Importe']],
        body: documentosData,
        theme: 'grid',
        headStyles: { 
          fillColor: [255, 255, 255], 
          textColor: [0, 0, 0], 
          fontStyle: 'bold', 
          halign: 'center',
          lineWidth: 0.5
        },
        bodyStyles: { 
          halign: 'center',
          lineWidth: 0.5
        },
        margin: { left: marginX, right: marginX },
        styles: { 
          fontSize: 9, 
          cellPadding: 4,
          lineWidth: 0.5,
          lineColor: [0, 0, 0]
        },
        tableLineWidth: 0.5,
        tableLineColor: [0, 0, 0]
      });

      currentY = (doc as any).lastAutoTable.finalY + 20;

      // Footer (formato exacto como el recibo)
      const pageHeight = doc.internal.pageSize.getHeight();
      const fechaImpresion = new Date().toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      const usuario = ordenPago.usuario || localStorage.getItem('userName') || localStorage.getItem('username') || 'Sistema';

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Impreso el: ${fechaImpresion}`, marginX, pageHeight - 30);
      doc.text(`Usuario: ${usuario}`, marginX, pageHeight - 15);

      // Generar blob y mostrar
      console.log('💾 Generando blob del PDF...');
      try {
        const pdfBlob = doc.output('blob');
        const url = window.URL.createObjectURL(pdfBlob);
        setPdfUrl(url);
        setSelectedOrdenPago(nroFormulario);
        console.log('✅ PDF generado exitosamente');
      } catch (error: any) {
        console.error('❌ Error al generar el blob del PDF:', error);
        throw new Error('Error al generar el archivo PDF: ' + (error?.message || 'Error desconocido'));
      }
    } catch (error: any) {
      console.error('❌ Error completo al generar el PDF:', error);
      const errorMessage = error?.message || 
                          error?.response?.data?.message || 
                          'Error al generar el PDF de la orden de pago';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setSelectedOrdenPago(null);
  };

  // Función para obtener el chip de estado según el CAE
  const getEstadoChip = (caeNro?: string) => {
    if (caeNro) {
      return {
        label: 'Con CAE',
        bgColor: '#4caf50',
        textColor: '#ffffff'
      };
    }
    return {
      label: 'Sin CAE',
      bgColor: '#ff9800',
      textColor: '#ffffff'
    };
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatComprobante = (ordenPago: OrdenPago) => {
    const { letra, nroSuc, nroComprobante } = ordenPago;
    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }

    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) {
        return ''.padStart(length, '0');
      }
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric.toString().padStart(length, '0');
      }
      return String(value).padStart(length, '0');
    };

    const sucursal = formatPart(nroSuc, 5);
    const comprobante = formatPart(nroComprobante, 8);
    const letraNormalizada = letra.trim().toUpperCase();

    if (!letraNormalizada) {
      return '-';
    }

    return `${letraNormalizada}-${sucursal}-${comprobante}`;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Tarjeta blanca contenedora principal */}
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con título y botones */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant={isSmallMobile ? "h5" : "h4"} 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2',
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              Orden de Pago
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleNuevaOrdenPago}
              fullWidth={isSmallMobile}
              sx={{
                borderRadius: 2,
                px: { xs: 2, sm: 3 },
                py: { xs: 1.5, sm: 1 },
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                }
              }}
            >
              {isSmallMobile ? 'Nueva' : 'Nueva Orden de Pago'}
            </Button>
          </Stack>

          {/* Campo de búsqueda y filtros */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Buscar por número, descripción, nombre o CUIT..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => {
                    setFechaHasta(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      }
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Tabla o Cards según el tamaño de pantalla */}
          {isMobile ? (
            <Stack spacing={2}>
              {ordenesPago.map((ordenPago) => {
                return (
                  <Card 
                    key={ordenPago.nroFormulario} 
                    sx={{ 
                      width: '100%',
                      borderRadius: 2,
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="start">
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Orden de Pago #{ordenPago.nroFormulario}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {ordenPago.nombre}
                            </Typography>
                          </Box>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Fecha:</strong> {new Date(ordenPago.fechaEmision).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Proveedor:</strong> {ordenPago.nroProveedor}
                        </Typography>
                        {ordenPago.cuit && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CUIT:</strong> {ordenPago.cuit}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          <strong>Importe:</strong> {formatCurrency(ordenPago.importeTotal)}
                        </Typography>
                        {ordenPago.usuario && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Usuario:</strong> {ordenPago.usuario}
                          </Typography>
                        )}
                        {ordenPago.cae_Nro && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>CAE:</strong> {ordenPago.cae_Nro}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Tooltip title="Imprimir PDF">
                            <IconButton
                              onClick={() => handleImprimirPDF(ordenPago.nroFormulario)}
                              size="small"
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                '&:hover': { bgcolor: '#1976d2' }
                              }}
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
              {loading ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: 6,
                  px: 2
                }}>
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ 
                      textAlign: 'center',
                      fontWeight: 500
                    }}
                  >
                    Cargando órdenes de pago...
                  </Typography>
                </Box>
              ) : ordenesPago.length === 0 && (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: 6,
                  px: 2
                }}>
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ 
                      textAlign: 'center',
                      mb: 1,
                      fontWeight: 500
                    }}
                  >
                    Aún no hay órdenes de pago cargadas
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ 
                      textAlign: 'center',
                      opacity: 0.7
                    }}
                  >
                    Las órdenes de pago aparecerán aquí cuando estén disponibles
                  </Typography>
                </Box>
              )}
              <TablePagination
                rowsPerPageOptions={[10]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Nro. Orden</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 110, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>Proveedor</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>CUIT</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} align="right">Importe</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: 'none' }}>Forma de Pago</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', lg: 'table-cell' } }}>Usuario</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: 'none' }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 5 : 11} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 1, fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          Cargando órdenes de pago...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : ordenesPago.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 5 : 11} align="center" sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 1, fontWeight: 500, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                          No se encontraron órdenes de pago con los filtros aplicados
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.7, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          Intenta ajustar los filtros de búsqueda o fechas
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    ordenesPago.map((ordenPago) => {
                      const estadoChip = getEstadoChip(ordenPago.cae_Nro);
                      const fechaEmisionDate = new Date(ordenPago.fechaEmision);
                      return (
                        <TableRow 
                          key={ordenPago.nroFormulario}
                          sx={{
                            '&:hover': {
                              bgcolor: '#f9f9f9',
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{ordenPago.nroFormulario}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>{fechaEmisionDate.toLocaleDateString()}</TableCell>
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' }, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {formatComprobante(ordenPago)}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>{ordenPago.nroProveedor}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, maxWidth: { xs: 120, sm: 'none' }, overflow: 'hidden', textOverflow: 'ellipsis' }}>{ordenPago.nombre}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', md: 'table-cell' } }}>{ordenPago.cuit || '-'}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: '#1976d2', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                            {formatCurrency(ordenPago.importeTotal)}
                          </TableCell>
                          <TableCell sx={{ display: 'none' }}>{ordenPago.formaDePago || '-'}</TableCell>
                          <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', lg: 'table-cell' } }}>{ordenPago.usuario || '-'}</TableCell>
                          <TableCell sx={{ display: 'none' }}>
                            <Chip
                              label={estadoChip.label}
                              size="small"
                              sx={{
                                bgcolor: estadoChip.bgColor,
                                color: estadoChip.textColor,
                                fontWeight: 500,
                                borderRadius: 1.5,
                                minWidth: 90,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Imprimir PDF">
                              <IconButton
                                onClick={() => handleImprimirPDF(ordenPago.nroFormulario)}
                                size="small"
                                sx={{
                                  bgcolor: '#2196f3',
                                  color: '#fff',
                                  borderRadius: 1.5,
                                  width: { xs: 28, sm: 32 },
                                  height: { xs: 28, sm: 32 },
                                  '&:hover': { 
                                    bgcolor: '#1976d2',
                                    transform: 'scale(1.1)',
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[10]}
                component="div"
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {pdfUrl && selectedOrdenPago && (
        <PDFViewer 
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Orden de Pago ${selectedOrdenPago}`}
          documentId={`${selectedOrdenPago}`}
          documentType="ordenPago"
        />
      )}
    </Box>
  );
};

export default OrdenPago;
